import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Spinner from '../components/Spinner';
import AuthService from '../services/authservices';
import { DashboardOutletContextValue } from '../types/dashboard';

type PreviewTableKey = 'pgs' | 'tgs' | 'pss' | 'pcs' | 'tss';

type TablePreviewSlice = {
  available: boolean;
  reason?: string;
  table?: string;
  columns?: string[];
  count?: number;
  total?: number;
  rows?: Record<string, unknown>[];
};

type PreviewMeta = {
  job_id: string;
  sport_code: string;
  season: number;
  team_scope_id: number | null;
  message?: string;
};

type SummaryResponse = PreviewMeta & {
  summary?: boolean;
  main_tables: {
    pgs?: TablePreviewSlice;
    tgs?: TablePreviewSlice;
    pss?: TablePreviewSlice;
    pcs?: TablePreviewSlice;
    tss?: TablePreviewSlice;
    pgs_total?: number;
    tgs_total?: number;
  };
};

type TableResponse = PreviewMeta & {
  table?: PreviewTableKey;
  pagination?: { limit: number; offset: number };
  main_tables: {
    pgs_table?: string;
    tgs_table?: string;
    pgs_columns?: string[];
    tgs_columns?: string[];
    pgs_count?: number;
    tgs_count?: number;
    pgs_total?: number;
    tgs_total?: number;
    pgs_rows?: Record<string, unknown>[];
    tgs_rows?: Record<string, unknown>[];
    pgs?: TablePreviewSlice;
    tgs?: TablePreviewSlice;
    pss?: TablePreviewSlice;
    pcs?: TablePreviewSlice;
    tss?: TablePreviewSlice;
  };
};

const authService = new AuthService();
const API_HOST = process.env.REACT_APP_API_HOST || 'http://localhost:8000';

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function looksLikeUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function compactUuid(value: string) {
  const v = value.trim();
  if (!looksLikeUuid(v)) return v;
  return `${v.slice(0, 8)}…${v.slice(-6)}`;
}

function formatCell(value: unknown) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && value.trim() === '') return '—';
  if (typeof value === 'string') return compactUuid(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function tableTotal(summary: SummaryResponse | null, key: PreviewTableKey): number {
  if (!summary?.main_tables) return 0;
  const slice = summary.main_tables[key];
  if (!slice?.available) return 0;
  if (key === 'pgs') return summary.main_tables.pgs_total ?? slice.total ?? 0;
  if (key === 'tgs') return summary.main_tables.tgs_total ?? slice.total ?? 0;
  return slice.total ?? slice.count ?? 0;
}

function Pagination({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (nextPage: number) => void;
}) {
  const visiblePages = useMemo(() => {
    const pages: number[] = [];
    const start = clamp(page - 2, 1, Math.max(1, pageCount));
    const end = clamp(page + 2, 1, Math.max(1, pageCount));
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [page, pageCount]);

  if (pageCount <= 1) return null;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <div className="text-xs font-medium text-slate-500">
        Page <span className="text-slate-800">{page}</span> of{' '}
        <span className="text-slate-800">{pageCount}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
            page <= 1
              ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          ← Prev
        </button>

        {visiblePages[0] !== 1 && (
          <>
            <button
              type="button"
              onClick={() => onPageChange(1)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              1
            </button>
            <span className="px-1 text-xs text-slate-400">…</span>
          </>
        )}

        {visiblePages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange(p)}
            className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
              p === page
                ? 'border-cyan-200 bg-cyan-50 text-cyan-800'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {p}
          </button>
        ))}

        {visiblePages[visiblePages.length - 1] !== pageCount && (
          <>
            <span className="px-1 text-xs text-slate-400">…</span>
            <button
              type="button"
              onClick={() => onPageChange(pageCount)}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              {pageCount}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
            page >= pageCount
              ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function DataTable({ rows, allColumns }: { rows: Record<string, unknown>[]; allColumns: string[] }) {
  const columns = allColumns;

  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-6 py-10 text-center text-sm text-slate-500">
        No rows returned for this page.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_54px_-40px_rgba(15,23,42,0.45)]">
      <div className="overflow-x-auto">
        <table className="min-w-[880px] w-full border-collapse">
          <thead>
            <tr className="bg-slate-50">
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-widest text-slate-400"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                {columns.map((col) => (
                  <td key={col} className="px-5 py-4 text-sm text-slate-600">
                    <span className="whitespace-nowrap">{formatCell(row[col])}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobPreviewPage() {
  const navigate = useNavigate();
  const { selectedSport } = useOutletContext<DashboardOutletContextValue>();
  const { jobId } = useParams();

  const [activeTab, setActiveTab] = useState<PreviewTableKey>('pgs');
  const [limit, setLimit] = useState(20);
  const [pagesByTab, setPagesByTab] = useState<Record<PreviewTableKey, number>>({
    pgs: 1,
    tgs: 1,
    pss: 1,
    pcs: 1,
    tss: 1,
  });

  const [meta, setMeta] = useState<PreviewMeta | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [tableData, setTableData] = useState<TableResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const page = pagesByTab[activeTab];

  const loadSummary = useCallback(async () => {
    if (!jobId) return;
    setSummaryLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_HOST}/jobs/${jobId}/preview`);
      url.searchParams.set('summary', 'true');
      const res = await authService.authenticatedFetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Unable to fetch preview summary.');
      }
      const payload = (await res.json()) as SummaryResponse;
      setSummary(payload);
      setMeta({
        job_id: payload.job_id,
        sport_code: payload.sport_code,
        season: payload.season,
        team_scope_id: payload.team_scope_id,
        message: payload.message,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unable to fetch preview summary.');
    } finally {
      setSummaryLoading(false);
    }
  }, [jobId]);

  const loadTable = useCallback(async () => {
    if (!jobId) return;
    setTableLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * limit;
      const url = new URL(`${API_HOST}/jobs/${jobId}/preview`);
      url.searchParams.set('table', activeTab);
      url.searchParams.set('limit', String(limit));
      url.searchParams.set('offset', String(offset));
      const res = await authService.authenticatedFetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Unable to fetch preview data.');
      }
      const payload = (await res.json()) as TableResponse;
      setTableData(payload);
      setMeta({
        job_id: payload.job_id,
        sport_code: payload.sport_code,
        season: payload.season,
        team_scope_id: payload.team_scope_id,
        message: payload.message,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unable to fetch preview data.');
    } finally {
      setTableLoading(false);
    }
  }, [activeTab, jobId, limit, page]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    void loadTable();
  }, [loadTable]);

  const totals = useMemo(
    () => ({
      pgs: tableTotal(summary, 'pgs'),
      tgs: tableTotal(summary, 'tgs'),
      pss: tableTotal(summary, 'pss'),
      pcs: tableTotal(summary, 'pcs'),
      tss: tableTotal(summary, 'tss'),
    }),
    [summary],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(totals[activeTab] / Math.max(1, limit)),
  );

  useEffect(() => {
    setPagesByTab((prev) => ({
      ...prev,
      [activeTab]: clamp(prev[activeTab], 1, pageCount),
    }));
  }, [activeTab, pageCount]);

  const activeSlice = tableData?.main_tables?.[activeTab];
  const rows =
    activeTab === 'pgs'
      ? tableData?.main_tables.pgs_rows ?? activeSlice?.rows ?? []
      : activeTab === 'tgs'
        ? tableData?.main_tables.tgs_rows ?? activeSlice?.rows ?? []
        : activeSlice?.rows ?? [];

  const columnsFromApi =
    activeTab === 'pgs'
      ? tableData?.main_tables.pgs_columns ?? activeSlice?.columns ?? []
      : activeTab === 'tgs'
        ? tableData?.main_tables.tgs_columns ?? activeSlice?.columns ?? []
        : activeSlice?.columns ?? [];

  const columns =
    columnsFromApi.length > 0
      ? columnsFromApi
      : rows.length > 0
        ? Object.keys(rows[0] || {})
        : [];

  const tabAvailable = (key: PreviewTableKey) =>
    summary?.main_tables?.[key]?.available !== false;

  const isLoading = summaryLoading || tableLoading;

  const setPageForActiveTab = (nextPage: number) => {
    const clamped = clamp(nextPage, 1, pageCount);
    setPagesByTab((prev) => ({ ...prev, [activeTab]: clamped }));
  };

  return (
    <section className="rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.18),_transparent_48%),linear-gradient(180deg,_#fdfefe_0%,_#f1f6ff_100%)] p-4 shadow-[0_30px_70px_-52px_rgba(15,23,42,0.65)] md:p-6">
      <div className="flex flex-col gap-4 border-b border-slate-200/80 pb-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Job Preview</h1>
          <p className="mt-1 text-sm text-slate-500">
            {meta ? (
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                  Job <span className="font-mono">{compactUuid(meta.job_id)}</span>
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                  Season {meta.season}
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
                  Team scope {meta.team_scope_id ?? '—'}
                </span>
              </span>
            ) : (
              'PGS/TGS preview rows'
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
          >
            ← Back
          </button>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Rows</span>
            <select
              value={limit}
              onChange={(e) => {
                setPagesByTab({ pgs: 1, tgs: 1, pss: 1, pcs: 1, tss: 1 });
                setLimit(Number(e.target.value));
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            >
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-center gap-2">
          {(['pgs', 'tgs', 'pss', 'pcs', 'tss'] as PreviewTableKey[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              disabled={summaryLoading && !summary}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                activeTab === key
                  ? 'border-cyan-200 bg-cyan-50 text-cyan-900'
                  : !tabAvailable(key) && summary
                    ? 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
              title={
                !tabAvailable(key) && summary
                  ? summary?.main_tables?.[key]?.reason || 'Unavailable'
                  : undefined
              }
            >
              {key.toUpperCase()}{' '}
              <span className="ml-2 text-xs font-semibold text-slate-500">
                ({summaryLoading ? '…' : totals[key]})
              </span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="mt-5 flex min-h-[260px] items-center justify-center rounded-[24px] border border-slate-200 bg-white/75">
            <Spinner />
          </div>
        ) : error ? (
          <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-6 py-10 text-center text-sm text-slate-500">
            {error}
          </div>
        ) : !tabAvailable(activeTab) ? (
          <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-white/70 px-6 py-10 text-center text-sm text-slate-500">
            {activeSlice?.reason || summary?.main_tables?.[activeTab]?.reason || 'Table unavailable.'}
          </div>
        ) : (
          <>
            <div className="mt-5">
              <DataTable rows={rows} allColumns={columns} />
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={setPageForActiveTab}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default JobPreviewPage;
